import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import Geohublogo from '../Images/Geohublogoo.png';
import html2canvas from 'html2canvas';

// Create a mock notification API for when the provider is not available
const mockNotificationAPI = {
  addNotification: () => null,
  updateNotification: () => null,
  removeNotification: () => null,
  clearAllNotifications: () => null
};

// Function to create a mock notification context
const createNotificationMock = () => mockNotificationAPI;

// Create a safe hook that won't throw errors if used outside a provider
let safeUseNotification;
try {
  const importedUseNotification = require('./NotificationSystem').useNotification;
  // Use a wrapper to catch errors when the hook is called
  safeUseNotification = () => {
    try {
      return importedUseNotification();
    } catch (error) {
      return mockNotificationAPI;
    }
  };
} catch (error) {
  // If import fails, provide a fallback that just returns the mock
  safeUseNotification = () => mockNotificationAPI;
}

// Initialize mermaid with higher rendering fidelity and performance settings
mermaid.initialize({
  startOnLoad: false, // We'll handle rendering manually
  theme: 'default',
  securityLevel: 'loose',
  themeVariables: {
    fontFamily: 'inherit',
    fontSize: '20px'
  },
  // Increase flowchart rendering performance
  flowchart: {
    useMaxWidth: false,
    htmlLabels: true,
    curve: 'basis'
  },
  // Ensure diagram fits available space
  gantt: {
    useWidth: 1000
  },
  // Add error handling for syntax errors
  logLevel: 'fatal',
  fontFamily: 'inherit'
});

// Add a global method to clear Mermaid cache
if (typeof window !== 'undefined') {
  window.mermaid = mermaid;
  
  // Add a global cleanup method to properly clear all mermaid instances and caches
  window.mermaid.clear = function() {
    try {
      // Clear internal mermaid cache
      if (window.mermaid.parse) {
        window.mermaid.parse.yy = {};
      }
      
      // Remove all rendered diagrams from DOM
      document.querySelectorAll('.mermaid').forEach(el => {
        if (el && el.parentNode) {
          el.parentNode.innerHTML = '';
        }
      });
      
      // Clear localStorage items related to mermaid
      const mermaidKeys = [
        'mermaid-diagram-cache',
        'mermaid-flowchart-config', 
        'mermaid-sequence-config',
        'mermaid-gantt-config',
        'mermaid-er-config',
        'mermaid-class-config'
      ];
      
      mermaidKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Failed to remove ${key} from localStorage`, e);
        }
      });
      
      // console.log('Mermaid cache fully cleared');
      return true;
    } catch (err) {
      console.error('Error clearing mermaid cache:', err);
      return false;
    }
  };
}

const MermaidLive = ({ diagramCode, className, onRenderComplete }) => {
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);
  const [renderingState, setRenderingState] = useState('loading'); // loading, rendered, error
  const [isFullyRendered, setIsFullyRendered] = useState(false);
  const [isReadyToShow, setIsReadyToShow] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [previewClicked, setPreviewClicked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const diagramHeight = useRef(null);
  const panelRef = useRef(null);
  const previewBoxRef = useRef(null);
  const renderAttempts = useRef(0);
  const timeoutRef = useRef(null);
  const notificationIdRef = useRef(null);
  
  // Use the safe hook that won't throw errors
  const notificationContext = safeUseNotification();
  
  const { addNotification, updateNotification, removeNotification } = notificationContext;
  
  // Function to clear invalid cached diagram data
  const clearInvalidCachedData = () => {
    try {
      // Look for cached diagrams or responses in localStorage
      const keysToCheck = [
        'cachedDiagrams',
        'mermaidDiagrams',
        'recentResponses',
        'storedResponses',
        'cachedResponses',
        'mermaid-diagram-cache',
        'mermaid-flowchart-config', 
        'mermaid-sequence-config'
      ];
      
      // Clear any keys that might contain invalid diagram data
      keysToCheck.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`Failed to remove ${key} from localStorage`, e);
        }
      });
      
      // console.log('Cleared potentially invalid diagram data from localStorage');
    } catch (error) {
      console.error('Error clearing cached data:', error);
    }
  };

  // Update the authentication check to clear invalid cached data
  useEffect(() => {
    // Check for chat limit redirect
    const fromChatLimit = sessionStorage.getItem('fromChatLimit') === 'true';
    
    // Simple check for auth token in localStorage
    const checkAuthStatus = () => {
      try {
        const token = localStorage.getItem('authToken');
        const user = localStorage.getItem('user');
        return !!(token && user);
      } catch (error) {
        console.error('Error checking auth status:', error);
        return false;
      }
    };
    
    const isUserAuthenticated = checkAuthStatus();
    setIsAuthenticated(isUserAuthenticated);
    
    // If coming from chat limit redirect, clear any cached diagram data
    if (fromChatLimit) {
      clearInvalidCachedData();
      if (!isUserAuthenticated) {
        setError('Please sign in to continue');
        setRenderingState('error');
        return;
      }
    }
    
    // If there's a potential auth/sign-in content in diagramCode and user is not authenticated
    // Set error state immediately and clear invalid cached data
    if (!isUserAuthenticated && diagramCode && (
      diagramCode.includes('Sign Up') || 
      diagramCode.includes('GEOHUB') || 
      diagramCode.includes('Syntax error') ||
      diagramCode.includes('Continue with Google')
    )) {
      setError('Authentication required to view diagrams');
      setRenderingState('error');
      
      // Clear potentially corrupted cached data
      clearInvalidCachedData();
      
      // Show notification about authentication
      addNotification('Please sign in to view diagrams', 'warning', 0);
    }
  }, [diagramCode]);

  // Main rendering logic
  useEffect(() => {
    if (!diagramCode) {
      setRenderingState('idle');
      return;
    }

    // Check for chat limit redirect
    const fromChatLimit = sessionStorage.getItem('fromChatLimit') === 'true';
    if (fromChatLimit) {
      setError('Please sign in to continue');
      setRenderingState('error');
      clearInvalidCachedData();
      return;
    }

    // If not authenticated and diagram looks like auth content, don't process
    if (!isAuthenticated && (
      diagramCode.includes("Sign Up") || 
      diagramCode.includes("Log in") || 
      diagramCode.includes("Syntax error") ||
      diagramCode.includes("GEOHUB") ||
      diagramCode.includes("Continue with Google")
    )) {
      setError("Authentication required to view diagrams");
      setRenderingState('error');
      clearInvalidCachedData();
      return;
    }

    // Validate if the diagram code is legitimate Mermaid syntax
    const isValidMermaidSyntax = (code) => {
      // Check for common valid mermaid diagram types
      const validStartPatterns = [
        /^\s*sequenceDiagram/i,
        /^\s*flowchart/i,
        /^\s*graph/i,
        /^\s*gantt/i,
        /^\s*classDiagram/i,
        /^\s*stateDiagram/i,
        /^\s*erDiagram/i,
        /^\s*journey/i,
        /^\s*pie/i
      ];
      
      return validStartPatterns.some(pattern => pattern.test(code));
    };

    // If the content doesn't look like valid Mermaid syntax
    if (!isValidMermaidSyntax(diagramCode)) {
      setError(`Invalid Mermaid diagram syntax. Diagram must start with a valid type declaration like 'flowchart', 'sequenceDiagram', etc.`);
      setRenderingState('error');
      
      // Notify about invalid syntax
      if (notificationIdRef.current) {
        removeNotification(notificationIdRef.current);
      }
      notificationIdRef.current = addNotification('Invalid Mermaid diagram syntax', 'error', 0);
      
      return;
    }

    // Reset all states when new diagram code is provided
    setRenderingState('loading');
    setIsFullyRendered(false);
    setIsReadyToShow(false);
    setSvg('');
    renderAttempts.current = 0;
    
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Clear previous notification if exists
    if (notificationIdRef.current) {
      removeNotification(notificationIdRef.current);
    }
    
    // Add loading notification
    notificationIdRef.current = addNotification('Preparing diagram...', 'info', 0);
    
    const renderDiagram = async () => {
      try {
        // First check if the diagram content might be from an auth modal
        if (diagramCode.includes('GEOHUB') || 
            diagramCode.includes('Sign Up') || 
            diagramCode.includes('Continue with Google') ||
            diagramCode.includes('Syntax error')) {
          throw new Error('Invalid diagram content - appears to be UI content instead of a valid diagram');
        }

        // Longer initial delay to ensure the loading state is visible
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Generate a unique ID for this render to avoid conflicts
        const uniqueId = `mermaid-diagram-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
        
        // console.log('Starting Mermaid rendering...');
        
        // Update notification
        updateNotification(notificationIdRef.current, { message: 'Rendering diagram...', type: 'info' });
        
        // Validate diagram syntax first
        try {
          await mermaid.parse(diagramCode);
        } catch (parseError) {
          throw new Error(`Syntax error: ${parseError.message || 'Invalid Mermaid syntax'}`);
        }
        
        // Render the diagram to SVG with a longer timeout
        const { svg } = await mermaid.render(uniqueId, diagramCode);
        
        // console.log('Mermaid SVG generated, processing...');
        
        // Store the SVG
        setSvg(svg);
        setError(null);
        
        // After setting SVG, wait longer for it to be fully processed by React
        timeoutRef.current = setTimeout(() => {
          setRenderingState('rendered');
          // console.log('Rendering state updated to rendered');
          
          // After the diagram is in the DOM, set fully rendered flag
          // This additional longer delay ensures the SVG is fully painted
          timeoutRef.current = setTimeout(() => {
            setIsFullyRendered(true);
            // console.log('Diagram marked as fully rendered');
            
            // Final delay before showing content to ensure everything is stable
            timeoutRef.current = setTimeout(() => {
              setIsReadyToShow(true);
              // console.log('Diagram ready to show');
              
              // Update notification to success
              updateNotification(notificationIdRef.current, { 
                message: 'Diagram loaded successfully', 
                type: 'success',
                duration: 3000
              });
              
              // Notify parent component that rendering is complete
              if (onRenderComplete) {
                onRenderComplete(true);
              }
            }, 500); // Additional 500ms wait before final display
          }, 500); // Increased to 500ms
        }, 500); // Increased to 500ms
      } catch (err) {
        console.error('Error rendering Mermaid diagram:', err);
        setError(err.message || 'Failed to render diagram');
        setRenderingState('error');
        
        if (renderAttempts.current < 2) {
          // Retry rendering with a longer delay
          renderAttempts.current += 1;
          // console.log(`Retrying diagram render (attempt ${renderAttempts.current})`);
          
          // Update notification
          updateNotification(notificationIdRef.current, { 
            message: `Retrying diagram render (attempt ${renderAttempts.current})`, 
            type: 'warning' 
          });
          
          timeoutRef.current = setTimeout(renderDiagram, 1000); // Increased to 1000ms
        } else {
          // Clear invalid cache data
          clearInvalidCachedData();
          
          // Update notification to error
          updateNotification(notificationIdRef.current, { 
            message: 'Failed to render diagram. Please try again.', 
            type: 'error',
            duration: 0 // Persistent until dismissed
          });
          
          // Notify parent component about error after exhausting retries
          if (onRenderComplete) {
            onRenderComplete(false, err.message);
          }
        }
      }
    };

    renderDiagram();
    
    // Cleanup function
    return () => {
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Clear notification
      if (notificationIdRef.current) {
        removeNotification(notificationIdRef.current);
      }
      // Reset states when component unmounts
      setIsFullyRendered(false);
      setIsReadyToShow(false);
    };
  }, [diagramCode, onRenderComplete, addNotification, updateNotification, removeNotification, isAuthenticated]);

  // Calculate and preserve height when diagram renders to prevent layout shifts
  useEffect(() => {
    if (renderingState === 'rendered' && svgRef.current) {
      const svgElement = svgRef.current.querySelector('svg');
      if (svgElement) {
        // Get the actual size of the rendered SVG
        const actualHeight = svgElement.getBoundingClientRect().height;
        
        // Store the actual dimensions for use in loading/error states
        diagramHeight.current = Math.max(actualHeight || 0, 400); // Ensure a minimum height
        
        // Ensure the SVG maintains its actual size
        svgElement.style.width = '100%';
        svgElement.style.maxWidth = '100%';
        svgElement.style.height = 'auto';
        
        // console.log(`Diagram height calculated: ${diagramHeight.current}px`);
      }
    }
  }, [renderingState, svg]);

  // Handle escape key press to close the panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isPanelOpen) {
        closePanel();
      }
    };

    // Add event listener for keydown
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPanelOpen]);

  // Focus management for the panel
  useEffect(() => {
    if (isPanelOpen && panelRef.current) {
      // Find the first focusable element in the panel (like the close button)
      const closeBtn = panelRef.current.querySelector('button');
      if (closeBtn) {
        closeBtn.focus();
      }
    }
  }, [isPanelOpen]);

  // Handle body scroll when panel is open
  useEffect(() => {
    if (isPanelOpen) {
      // Prevent scrolling on the main body
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling
      document.body.style.overflow = '';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = '';
    };
  }, [isPanelOpen]);

  const openPanel = () => {
    setIsPanelOpen(true);
    setPreviewClicked(true);
    
    // Send notification that panel was opened
    addNotification('Diagram opened in side panel', 'info', 2000);
  };

  const closePanel = () => {
    setIsPanelOpen(false);
    
    // Send notification that panel was closed
    addNotification('Diagram panel closed', 'info', 2000);
    
    // Return focus to the preview box
    if (previewBoxRef.current) {
      setTimeout(() => {
        previewBoxRef.current.focus();
      }, 100);
    }
  };

  const handleRetry = () => {
    // Notify about retry
    addNotification('Retrying diagram render...', 'warning', 3000);
    
    setRenderingState('loading');
    setIsFullyRendered(false);
    setIsReadyToShow(false);
    
    // Re-trigger the rendering process
    const renderDiagram = async () => {
      try {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Generate a unique ID for this retry
        const uniqueRetryId = `mermaid-retry-${Date.now()}`;
        const { svg } = await mermaid.render(uniqueRetryId, diagramCode);
        setSvg(svg);
        setError(null);
        
        timeoutRef.current = setTimeout(() => {
          setRenderingState('rendered');
          
          // Longer delay to ensure the SVG is fully rendered in the DOM
          timeoutRef.current = setTimeout(() => {
            setIsFullyRendered(true);
            
            // Final delay before showing
            timeoutRef.current = setTimeout(() => {
              setIsReadyToShow(true);
              
              // Notify success
              addNotification('Diagram successfully rendered after retry', 'success', 3000);
              
              if (onRenderComplete) onRenderComplete(true);
            }, 500);
          }, 500);
        }, 500);
      } catch (err) {
        console.error('Error re-rendering Mermaid diagram:', err);
        setError(err.message || 'Failed to render diagram');
        setRenderingState('error');
        
        // Notify failure
        addNotification('Retry failed. Unable to render diagram.', 'error', 0);
        
        if (onRenderComplete) onRenderComplete(false, err.message);
      }
    };
    
    renderDiagram();
  };

  const handleDownload = (format = 'svg') => {
    if (!svgRef.current || !svgRef.current.innerHTML) {
      console.error('No SVG content to download.');
      addNotification('Unable to download diagram. No content available.', 'error', 3000);
      return;
    }

    const EXPORT_WIDTH = 1200;
    const EXPORT_HEIGHT = 2000;

    let svgData = svgRef.current.innerHTML;
    const diagramTitle = diagramCode.split('\n')[0].replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'mermaid_diagram';

    // Add notification that download is starting
    addNotification(`Preparing ${format.toUpperCase()} download...`, 'info', 2000);

    // Parse the SVG string and inject the logo as an <image> tag
    const parser = new window.DOMParser();
    const svgDoc = parser.parseFromString(svgData, 'image/svg+xml');
    const svgElem = svgDoc.querySelector('svg');
    if (!svgElem) {
      console.error('SVG element not found.');
      addNotification('Download failed. SVG element not found.', 'error', 3000);
      return;
    }

    // Save original viewBox or calculate if missing
    let viewBox = svgElem.getAttribute('viewBox');
    if (!viewBox) {
      // fallback: use width/height
      const w = svgElem.getAttribute('width') || 800;
      const h = svgElem.getAttribute('height') || 600;
      viewBox = `0 0 ${w} ${h}`;
      svgElem.setAttribute('viewBox', viewBox);
    }

    // Set export size
    svgElem.setAttribute('width', EXPORT_WIDTH);
    svgElem.setAttribute('height', EXPORT_HEIGHT);

    // Insert logo as <image> at the top left corner
    const logoImg = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'image');
    logoImg.setAttributeNS('http://www.w3.org/1999/xlink', 'href', Geohublogo);
    logoImg.setAttribute('x', '0');
    logoImg.setAttribute('y', '0');
    logoImg.setAttribute('width', EXPORT_WIDTH * 0.15);
    const LOGO_HEIGHT = 40; // Set logo height (adjust as needed)
    logoImg.setAttribute('height', LOGO_HEIGHT);
    logoImg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
    svgElem.insertBefore(logoImg, svgElem.firstChild);

    // Shift all other SVG content down by the logo height
    const children = Array.from(svgElem.childNodes).filter(node => node !== logoImg);
    const g = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('transform', `translate(0, ${LOGO_HEIGHT})`);
    children.forEach(child => g.appendChild(child));
    svgElem.appendChild(g);

    // Increase SVG height to account for logo
    const origHeight = parseFloat(svgElem.getAttribute('height')) || EXPORT_HEIGHT;
    svgElem.setAttribute('height', origHeight + LOGO_HEIGHT);

    // Serialize back to string
    const serializer = new window.XMLSerializer();
    svgData = serializer.serializeToString(svgElem);

    if (format === 'svg') {
      // Download the SVG directly
      const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${diagramTitle}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Notify success
      addNotification('SVG downloaded successfully', 'success', 3000);
    } else if (format === 'png') {
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = EXPORT_WIDTH;
      canvas.height = EXPORT_HEIGHT;
      const ctx = canvas.getContext('2d');

      // Fill background
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);

      // Draw SVG (with logo) as image
      const img = new window.Image();
      img.onload = function () {
        try {
          ctx.drawImage(img, 0, 0, EXPORT_WIDTH, EXPORT_HEIGHT);
          const pngUrl = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = pngUrl;
          link.download = `${diagramTitle}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Notify success
          addNotification('PNG downloaded successfully', 'success', 3000);
        } catch (err) {
          console.error('Error generating PNG:', err);
          addNotification('Failed to generate PNG. The diagram may be too complex.', 'error', 3000);
        }
      };
      img.onerror = function(err) {
        console.error('Error loading SVG for PNG conversion:', err);
        addNotification('Failed to convert diagram to PNG', 'error', 3000);
      };
      img.src = 'data:image/svg+xml;base64,' + window.btoa(unescape(encodeURIComponent(svgData)));
    }
  };

  // Loading spinner component with accessibility
  const LoadingSpinner = () => (
    <div 
      className="mermaid-loading" 
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        backgroundColor: 'white',
        borderRadius: '8px',
        minHeight: diagramHeight.current ? `${diagramHeight.current}px` : '300px',
        width: '100%',
        textAlign: 'center',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}
      role="status"
      aria-live="polite"
    >
      <div className="spinner" style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        border: '3px solid rgba(0, 0, 0, 0.1)',
        borderTopColor: '#2241DD',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }} />
      <div className="mermaid-loading-text" style={{ 
        fontWeight: 500, 
        color: '#555',
        fontSize: '16px',
        marginBottom: '8px'
      }}>
        Rendering diagram...
      </div>
      <div style={{ 
        fontSize: '14px', 
        color: '#6c757d',
        maxWidth: '280px',
        lineHeight: '1.5'
      }}>
        This may take a moment depending on the complexity of the diagram.
      </div>
      <style jsx>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <span className="sr-only" style={{ 
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        borderWidth: '0'
      }}>
        Mermaid diagram is being rendered. This may take a few moments.
      </span>
    </div>
  );

  // Error display component with retry option
  const ErrorDisplay = ({ message, onRetry }) => {
    // Check if this is an authentication error
    const isAuthError = message && (
      message.includes("authentication") || 
      message.includes("sign in") || 
      message.includes("GEOHUB")
    );
    
    return (
      <div 
        className="mermaid-error" 
        style={{ 
          color: isAuthError ? '#3949ab' : '#e53e3e', 
          padding: '28px 24px',
          backgroundColor: 'white',
          borderRadius: '8px',
          borderLeft: `4px solid ${isAuthError ? '#3949ab' : '#e53e3e'}`,
          margin: '0',
          minHeight: diagramHeight.current ? `${diagramHeight.current}px` : '300px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}
        role="alert"
        aria-live="assertive"
      >
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '12px' }}>
            {isAuthError ? (
              // Lock icon for auth errors
              <>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </>
            ) : (
              // Alert icon for normal errors
              <>
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </>
            )}
          </svg>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '18px' }}>
            {isAuthError ? 'Authentication Required' : 'Error rendering diagram'}
          </p>
        </div>
        
        {isAuthError ? (
          <div style={{
            margin: '8px 0 24px 0',
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#555'
          }}>
            <p>You need to be signed in to view and render Mermaid diagrams.</p>
            <p>Please sign in to your GeoHub account to continue.</p>
          </div>
        ) : (
          <pre style={{ 
            margin: '8px 0 24px 0', 
            fontSize: '14px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            backgroundColor: '#fff5f5',
            padding: '16px',
            borderRadius: '6px',
            maxHeight: '200px',
            overflowY: 'auto',
            fontFamily: 'monospace'
          }}>
            {message}
          </pre>
        )}
        
        {isAuthError ? (
          <button
            onClick={() => window.location.href = '/login'}
            style={{
              alignSelf: 'flex-start',
              padding: '10px 18px',
              backgroundColor: '#3949ab',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#303f9f'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3949ab'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
              <polyline points="10 17 15 12 10 7"></polyline>
              <line x1="15" y1="12" x2="3" y2="12"></line>
            </svg>
            Sign In
          </button>
        ) : onRetry && (
          <button
            onClick={onRetry}
            style={{
              alignSelf: 'flex-start',
              padding: '10px 18px',
              backgroundColor: '#e53e3e',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c53030'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#e53e3e'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <path d="M3 2v6h6"></path>
              <path d="M3 13a9 9 0 1 0 3-7.7L3 8"></path>
            </svg>
            Retry Rendering
          </button>
        )}
      </div>
    );
  };

  // Preview box component
  const PreviewBox = () => (
    <button
      ref={previewBoxRef}
      className="mermaid-preview-box"
      onClick={openPanel}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        border: '1px dashed #ced4da',
        borderRadius: '6px',
        cursor: 'pointer',
        width: '100%',
        minHeight: '200px',
        transition: 'all 0.2s ease',
        color: '#495057',
        textAlign: 'center',
        marginBottom: '20px',
        position: 'relative'
      }}
      aria-label="Open Mermaid diagram in side panel"
      role="button"
      aria-haspopup="dialog"
    >
      {/* Status indicator */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: 
          renderingState === 'error' ? '#e53e3e' : 
          isReadyToShow ? '#2e7d32' : 
          '#f9a825',
        transition: 'background-color 0.3s ease',
        boxShadow: '0 0 4px rgba(0, 0, 0, 0.2)'
      }} />
      
      <div style={{
        marginBottom: '10px',
        fontSize: '28px',
        color: '#2241DD'
      }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
        </svg>
      </div>
      <div style={{ fontWeight: 500, fontSize: '16px' }}>
        Click to view diagram
      </div>
      <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '8px' }}>
        {isReadyToShow ? 'Diagram is ready to view' : renderingState === 'error' ? 'Error rendering diagram' : 'Diagram is being prepared...'}
      </div>
    </button>
  );

  // Side Panel component
  const SidePanel = () => (
    <div
      className="mermaid-side-panel-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'flex-end',
        opacity: isPanelOpen ? 1 : 0,
        visibility: isPanelOpen ? 'visible' : 'hidden',
        transition: 'opacity 0.35s ease, visibility 0.35s ease',
        willChange: 'opacity, visibility'
      }}
      onClick={(e) => {
        // Close if clicking the overlay (outside the panel)
        if (e.target === e.currentTarget) {
          closePanel();
        }
      }}
      aria-hidden={!isPanelOpen}
    >
      <div
        ref={panelRef}
        className="mermaid-side-panel"
        style={{
          width: '450px',
          maxWidth: '95vw',
          height: '100%',
          backgroundColor: 'white',
          boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.2)',
          borderLeft: '1px solid rgba(0, 0, 0, 0.1)',
          padding: 0,
          overflowY: 'hidden',
          transform: isPanelOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          display: 'flex',
          flexDirection: 'column',
          willChange: 'transform'
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="panel-title"
      >
        {/* Header with brand logo and close button */}
        <div 
          className="panel-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderBottom: '1px solid #e6e8eb',
            backgroundColor: '#ffffff'
          }}
        >
          <div 
            className="panel-header-logo"
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img 
              src={Geohublogo} 
              alt="GeoHub Logo" 
              style={{
                height: '36px',
                objectFit: 'contain'
              }}
            />
          </div>
          <button
            onClick={closePanel}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              fontWeight: 300,
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '4px',
              lineHeight: 1,
              color: '#6c757d',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '-8px'
            }}
            aria-label="Close panel"
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Main content area with auto scroll */}
        <div 
          style={{ 
            flex: 1, 
            overflowY: 'auto',
            padding: '24px', 
            backgroundColor: '#f9fafb'
          }}
        >
          {renderingState === 'loading' && <LoadingSpinner />}
          {renderingState === 'error' && (
            <ErrorDisplay 
              message={error} 
              onRetry={handleRetry}
            />
          )}
          {renderingState === 'rendered' && (
            <div 
              style={{ 
                position: 'relative', 
                width: '100%',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
              }}
            >
              <div 
                id="diagram-export-container"
                ref={svgRef}
                className={`mermaid-diagram ${className || ''}`}
                dangerouslySetInnerHTML={{ __html: svg }}
                style={{
                  overflowX: 'auto',
                  padding: '8px 0',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  transition: 'opacity 0.5s ease',
                  opacity: isReadyToShow ? 1 : 0, // Only show when fully rendered and ready
                  width: '100%',
                  maxWidth: '100%'
                }}
                tabIndex={0}
                aria-label="Mermaid diagram visualization"
              />
              {svg && isReadyToShow && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  display: 'flex',
                  gap: '5px',
                  zIndex: 10
                }}>
                  <button 
                    onClick={async () => {
                      const container = document.getElementById('diagram-export-container');
                      if (!container) {
                        addNotification('Diagram container not found.', 'error', 3000);
                        return;
                      }
                      html2canvas(container, { useCORS: true, backgroundColor: '#fff' }).then(canvas => {
                        const link = document.createElement('a');
                        link.download = 'diagram.png';
                        link.href = canvas.toDataURL();
                        link.click();
                      });
                    }}
                    title="Download as PNG"
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      backgroundColor: '#2241DD',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease'
                    }}
                    aria-label="Download diagram as PNG"
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1a33b0'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2241DD'}
                  >
                    {/* Download icon SVG */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 0 }}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer area for future controls */}
        <div 
          className="panel-footer"
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e6e8eb',
            backgroundColor: '#ffffff',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}
        >
          {/* Reserved for future controls */}
        </div>
      </div>
    </div>
  );

  return (
    <div 
      ref={containerRef}
      className={`mermaid-container ${className || ''}`}
      style={{ 
        position: 'relative', 
        margin: '16px 0',
        width: '100%',
        maxWidth: '100%',
        overflow: 'visible'
      }}
      data-rendering-complete={isReadyToShow ? 'true' : 'false'}
    >
      {/* Preview box (always shown) */}
      <PreviewBox />
      
      {/* Side panel (shown when opened) */}
      <SidePanel />
      
      {/* Main content below the preview box (only shown when conditions are met) */}
      {previewClicked && isReadyToShow && (
        <div className="mermaid-content" style={{ marginTop: '20px' }}>
          {/* This area is for additional content related to the diagram, if any */}
        </div>
      )}
    </div>
  );
};

export default MermaidLive; 